/* eslint-disable @typescript-eslint/restrict-template-expressions */
import '../../../components/JobQueue/JobQueue.scss';

import GetAppIcon from '@mui/icons-material/GetApp';
import { IconButton, Tab, Tabs } from '@mui/material';
import dividerLogo from 'assets/images/split-v.svg';
import { JobData } from 'components/JobQueue/utils';
import { Loader } from 'components/loader';
import saveAs from 'file-saver';
import React, { useCallback, useEffect, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useLocation, useParams } from 'react-router-dom';
import { getAllListData } from 'services/CommonServices';
import { useAppSelector } from 'store/hooks';
import { MASTER_ENDPOINT } from 'utils/Constant';

import { CandidateHeader } from './components/CandidateHeader';
import { TabContent } from './components/TabContent';
import { PdfViewer } from './viewer';

type Props = {
  visible?: boolean;
  onClose?: () => void;
  resumeId?: string;
  fileId?: string;
  candidateId?: string;
  candidateData?: any;
  jobData?: any;
  fileName?: string;
  showDownload?: boolean;
  showShortlist?: boolean;
  onDownload?: () => void;
  onShortlist?: () => void;
  widthType?: string;
  jobId?: string;
};

export const IPriseJobsPreview: React.FC<Props> = (props: Props) => {
  const location = useLocation();
  const Params = location?.state?.Params;
  const { userInfo }: any = useAppSelector((store: any) => store.userLogin);
  const candidateDetails = location?.state?.candidateDetails;
  const {
    visible = true,
    // onClose,
    // fileId: propFileId,
    candidateId,
    candidateData: propCandidateData,
    jobData: propJobData,
    fileName,
    showDownload = false,
    // showShortlist,
    onDownload,
    onShortlist,
    widthType,
    jobId: propJobId,
  } = props;

  const {
    jobId: paramJobId,
    candidateId: paramCandidateId,
    // fileId: paramFileId,
  } = useParams<{
    jobId?: string;
    candidateId?: string;
    fileId?: string;
  }>();

  const [activeTab, setActiveTab] = useState(0);
  const [candidateData, setCandidateData] = useState<any>(
    propCandidateData || {},
  );
  const [jobData, setJobData] = useState<JobData>(propJobData || {});
  const [loader, setLoader] = useState<boolean>(
    !propCandidateData && !propJobData,
  );
  const [getOverviewData, setGetOverviewData] = useState<any>({
    withGap: '',
    withoutGap: '',
  });
  // Use jobId and candidateId from props or params
  const jobId = propJobId || paramJobId;
  const finalCandidateId = candidateId || paramCandidateId;

  // Use fileId from props or params
  // const fileId = propFileId || paramFileId || '';

  const getCandidateData = useCallback(async (id: string) => {
    if (!id) return;

    setLoader(true);
    await getAllListData(`${MASTER_ENDPOINT.Candidates}?id=${id}`)
      .then((res) => {
        setCandidateData(res?.data?.[0] || {});
      })
      .catch((err: any) => {
        console.error('Error fetching candidate data:', err);
      })
      .finally(() => {
        setLoader(false);
      });
  }, []);
  const onOverviewDownload = useCallback(() => {
    if (getOverviewData?.withGap) {
      const url = `${process.env.REACT_APP_APIURL}${
        MASTER_ENDPOINT.Files
      }/${String(getOverviewData?.withGap)}`;
      const fileName = `${
        candidateDetails?.candidate?.firstName
          ? candidateDetails?.candidate?.firstName
          : '-'
      }_${
        candidateDetails?.candidate?.lastName
          ? candidateDetails?.candidate?.lastName
          : ''
      }_Overview`;
      saveAs(url, fileName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getOverviewData]);
  const getJobData = useCallback(
    async (id: string) => {
      if (!id || propJobData) return;
      await getAllListData(`${MASTER_ENDPOINT.Jobs}?id=${id}`)
        .then((res) => {
          setJobData(res?.data?.[0] || {});
        })
        .catch((err: any) => {
          console.error('Error fetching job data:', err);
        });
    },
    [propJobData],
  );

  useEffect(() => {
    if (finalCandidateId && !propCandidateData) {
      getCandidateData(finalCandidateId);
    } else if (propCandidateData) {
      setCandidateData(propCandidateData);
      setLoader(false);
    }
    if (jobId && !propJobData) {
      getJobData(jobId);
    } else if (propJobData) {
      setJobData(propJobData);
    }
  }, [
    finalCandidateId,
    jobId,
    propCandidateData,
    propJobData,
    getCandidateData,
    getJobData,
  ]);

  useEffect(() => {
    if (candidateDetails?.id) {
      getAllListData(
        `jobs/${MASTER_ENDPOINT.Candidates}/overview-file/${candidateDetails?.id}`,
      )
        .then((res) => {
          setGetOverviewData({
            withGap: res?.jobOverviewFileId,
            withoutGap: res?.jobOverviewWithoutGapsFileId,
          });
        })
        .catch((err: any) => {
          console.error('Error fetching candidate data:', err);
        })
        .finally(() => {
          setLoader(false);
        });
    }
  }, [candidateDetails]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (!visible) return null;

  // Format candidate data for header
  const candidateName =
    candidateDetails?.candidate?.firstName &&
    candidateDetails?.candidate?.lastName
      ? `${String(candidateDetails.candidate.firstName)} ${String(
          candidateDetails.candidate.lastName,
        )}`
      : candidateDetails?.name;
  const candidateInternalId = candidateDetails?.candidate?.id || '';

  const availability = candidateDetails?.candidate?.benchStatus || '';
  const availabilityDate = candidateDetails?.candidate?.benchAvailability
    ? candidateDetails?.candidate?.benchAvailability
    : undefined;
  const sourceLink = candidateDetails?.jobSourceUrl || '';
  const candidateEmail = candidateDetails?.candidate?.email || '';
  const candidatePhone =
    candidateDetails?.candidate?.phone ||
    candidateDetails?.candidate?.mobileNumber ||
    '';
  const candidateLocation =
    candidateDetails?.candidate?.city && candidateDetails?.candidate?.state
      ? `${String(candidateDetails.candidate.city)}, ${String(
          candidateDetails.candidate.state,
        )}`
      : candidateDetails?.location || '';

  const matchScore =
    candidateDetails?.matchPercentage || candidateDetails?.matchScore;
  const visaStatus =
    candidateDetails?.workStatus?.name || candidateDetails?.workAuth || '';
  // Calculate pay rate from job or candidate
  const payRate = ((): string => {
    // if (candidateData?.payRate) return `$${String(candidateData.payRate)}`;
    if (jobData?.payRangeFrom && jobData?.payRangeTo) {
      return `$${jobData.payRangeFrom} - $${jobData.payRangeTo}`;
    } else if (jobData?.payRangeFrom) {
      return `$${jobData.payRangeFrom}`;
    }
    return '';
  })();

  const source = candidateData?.source || jobData?.jobSource || '';

  const defaultLayout = [
    widthType === 'Sales'
      ? 50
      : widthType === 'recruiterRead' || widthType === 'recruiter'
      ? 45
      : 50,
    widthType === 'Sales'
      ? 50
      : widthType === 'recruiterRead' || widthType === 'recruiter'
      ? 55
      : 50,
  ];

  const TabsList = [
    {
      label: 'Overview',
      value: 0,
    },
    {
      label: 'Notes',
      value: 1,
    },
    {
      label: 'Attachments',
      value: 2,
    },
  ];
  if (loader) {
    return (
      <div
        className="inter-font-family"
        style={{
          alignItems: 'center',
          backgroundColor: '#ffffff',
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
          width: '100%',
        }}>
        <Loader newLoader />
      </div>
    );
  }

  return (
    <div
      className="inter-font-family"
      style={{
        backgroundColor: '#ffffff',
        display: 'flex',
        height: 'calc(100vh - 70px)',
        overflow: 'hidden',
        width: '100%',
      }}>
      <PanelGroup direction="horizontal" id="ipr-preview-group">
        <Panel defaultSize={defaultLayout[0]} minSize={35}>
          <div
            style={{
              borderRight: '1px solid #e5e7eb',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
            }}>
            {/* Header Section */}
            <CandidateHeader
              candidateName={candidateName}
              availability={availability}
              availabilityDate={availabilityDate}
              email={candidateEmail}
              phone={candidatePhone}
              location={candidateLocation}
              matchScore={matchScore}
              visaStatus={visaStatus}
              payRate={payRate}
              source={source}
              jobTitle={jobData?.jobTitle}
              showDownload={showDownload}
              showShortlist={candidateDetails?.isShortlisted}
              onDownload={onDownload}
              onShortlist={onShortlist}
              onShortlistForJob={onShortlist}
              candidateInternalId={candidateInternalId}
              getOverviewId={getOverviewData}
              jobMatchCandidateId={candidateDetails?.id}
              candidateId={candidateDetails?.candidate?.id}
              candidateDetails={candidateDetails}
              Params={Params}
              sourceLink={sourceLink}
              goal={jobData?.goal}
              expFrom={jobData?.expFrom}
              expTo={jobData?.expTo}
            />

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                '& .Mui-selected': {
                  color: '#000000 !important',
                },
                '& .MuiTab-root': {
                  color: '#000000',
                  fontFamily: 'Inter',
                  fontSize: '13px',
                  minHeight: '48px',
                  padding: '8px 4px',
                  textTransform: 'none',
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#be0000',
                  height: '2px',
                },
                borderBottom: '1px solid #ececec',
                gap: '12px',
                paddingLeft: '5px',
              }}>
              {TabsList.map((tab) => (
                <Tab
                  sx={{
                    '&.Mui-selected': {
                      color: '#000000 !important',
                    },
                    fontWeight: activeTab === tab.value ? 500 : 400,
                    lineHeight: 'normal',
                    opacity: activeTab === tab.value ? 1 : 0.6,
                  }}
                  key={tab.value}
                  label={tab.label}
                />
              ))}
              {activeTab === 0 && getOverviewData && (
                <IconButton
                  onClick={onOverviewDownload}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                    },
                    backgroundColor: '#ffffff',
                    border: '1px solid #dddddd',
                    borderRadius: '6px',
                    height: '32px',
                    marginLeft: 'auto',
                    marginRight: '20px',
                    marginTop: '8px',
                    padding: '6px',
                    width: '32px',
                  }}>
                  <GetAppIcon sx={{ color: '#616161', fontSize: '16px' }} />
                </IconButton>
              )}
            </Tabs>

            {/* Tab Content */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <TabContent
                activeTab={activeTab}
                candidateData={candidateDetails || candidateData}
                candidateId={finalCandidateId}
                jobData={jobData}
              />
            </div>
          </div>
        </Panel>

        <PanelResizeHandle className="panel-resize-handle">
          <img src={dividerLogo} className="handle-bar" alt="resize" />
        </PanelResizeHandle>

        <Panel defaultSize={defaultLayout[1]} minSize={35}>
          <div
            style={{
              backgroundColor: '#f9fafb',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minWidth: 0,
              overflow: 'hidden',
              position: 'relative',
            }}>
            <div
              style={{
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #e5e7eb',
                position: 'sticky',
                top: 0,
                zIndex: 10,
              }}>
              {/* PDF Toolbar will be fixed here */}
            </div>
            <div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
              {candidateDetails?.candidate?.id ? (
                <PdfViewer
                  url={`${process.env.REACT_APP_APIURL}${MASTER_ENDPOINT.Files}/${candidateDetails.candidate.id}?type=resume&userId=${userInfo?.id}`}
                  hideDownload={false}
                  fileName={fileName}
                  candidateId={finalCandidateId}
                />
              ) : null}
            </div>
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
};
